import React from 'react';
import PropTypes from 'prop-types';
import invert from 'lodash/invert';
import { withRouter } from 'react-router-dom';
import { gettext } from '@eb/i18n';
import queryString from 'query-string';

import FilterSection from './FilterSection';
import DateFilterSection from './DateFilterSection';
import FilterCheckboxGroup from './FilterCheckboxGroup';

import LazyInlineDatePicker from './LazyInlineDatePicker';

import {
    EVENTBRITE_FORMAT_MAPPING_SEARCH,
    EVENTBRITE_FORMAT_TO_REFINEMENT,
    EVENTBRITE_CATEGORY_MAPPING_SEARCH,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    DATES_MAPPING_SEARCH,
    DATES_TO_REFINEMENT,
    PRICE_MAPPING,
} from '../../../../constants';

import {
    DATE_FILTER,
    TAGS_FILTER,
    FORMAT_FILTER,
    PRICE_FILTER,
    LANGUAGE_FILTER,
    CURRENCY_FILTER,
    FORMATTED_CURRENCIES,
    FORMATTED_LANGUAGES,
} from '../../../../constants/filters';

const DateFilters = (props) => {
    const urlParams = queryString.parse(location.search.replace('?', ''));

    return (
        <DateFilterSection
            filters={{
                all: gettext('Any day'),
                ...DATES_MAPPING_SEARCH,
            }}
            onOptionChange={(value, redirect) =>
                props.onFilterChange(DATE_FILTER, value, redirect)
            }
            onCustomDate={(startDate, endDate) =>
                props.onCustomDateChange(startDate, endDate)
            }
            onDatePickerSelected={
                props.isMobile
                    ? () => props.setDatePickerDrawerVisible(true)
                    : () => {}
            }
            DatePickerComponent={
                <LazyInlineDatePicker
                    onDatePickerChange={props.onCustomDateChange}
                    urlParams={urlParams}
                />
            }
            urlParameterMapping={invert(DATES_TO_REFINEMENT)}
            isMobile={props.isMobile}
            isDrawerVisible={props.isDatePickerDrawerVisible}
        />
    );
};

const CategoryFilters = (props) => (
    <FilterSection
        isMobile={props.isMobile}
        filters={{
            all: gettext('Any category'),
            ...EVENTBRITE_CATEGORY_MAPPING_SEARCH,
        }}
        onOptionChange={(value, redirect) =>
            props.onFilterChange(TAGS_FILTER, value, redirect)
        }
        urlParameterMapping={invert(EVENTBRITE_CATEGORY_TO_REFINEMENT)}
    />
);

const FormatFilters = (props) => (
    <FilterSection
        isMobile={props.isMobile}
        filters={{
            all: gettext('Any format'),
            ...EVENTBRITE_FORMAT_MAPPING_SEARCH,
        }}
        onOptionChange={(value, redirect) =>
            props.onFilterChange(FORMAT_FILTER, value, redirect)
        }
        urlParameterMapping={invert(EVENTBRITE_FORMAT_TO_REFINEMENT)}
    />
);

const LanguageFilters = (props) => (
    <FilterSection
        isMobile={props.isMobile}
        filters={{
            all: gettext('Any language'),
            ...FORMATTED_LANGUAGES,
        }}
        onOptionChange={(value, redirect) =>
            props.onFilterChange(LANGUAGE_FILTER, value, redirect)
        }
        urlParameterMapping={{
            lang: LANGUAGE_FILTER,
            isInQuery: true,
        }}
        //Custom render necessary for multiselect style filters
        renderFilters={(filters, selectedFilter, onClick) => {
            return (
                <div className="filter-section__checkbox-group">
                    <FilterCheckboxGroup
                        values={filters}
                        selectedFilters={selectedFilter}
                        onClick={(values) => onClick(values, true, true)}
                        name={`language-options--${
                            props.isMobile ? 'mobile' : 'desktop'
                        }`}
                        isMobile={props.isMobile}
                    />
                </div>
            );
        }}
    />
);

const CurrencyFilters = (props) => (
    <FilterSection
        isMobile={props.isMobile}
        filters={{
            all: gettext('Any currency'),
            ...FORMATTED_CURRENCIES,
        }}
        onOptionChange={(value, redirect) =>
            props.onFilterChange(CURRENCY_FILTER, value, redirect)
        }
        urlParameterMapping={{
            cur: CURRENCY_FILTER,
            isInQuery: true,
        }}
    />
);

const PriceFilters = (props) => {
    return (
        <div className="eds-l-mar-bot-20">
            <FilterSection
                isMobile={props.isMobile}
                filters={{
                    all: gettext('Any price'),
                    ...PRICE_MAPPING,
                }}
                onOptionChange={(value, redirect) =>
                    props.onFilterChange(PRICE_FILTER, value, redirect)
                }
                useAppState={true}
                activeFilterOption={
                    props.selectedFilters[PRICE_FILTER]
                        ? props.selectedFilters[PRICE_FILTER]
                        : 'all'
                }
            />
        </div>
    );
};

const shouldShowFilterSection = (
    filterTypeToShow,
    isDatePickerDrawerVisible,
    filterTypeConstant,
) => {
    if (isDatePickerDrawerVisible) {
        return filterTypeConstant === DATE_FILTER;
    }

    return (
        !filterTypeToShow ||
        (filterTypeToShow && filterTypeToShow === filterTypeConstant)
    );
};

const Filters = (props) => {
    const shouldShow = shouldShowFilterSection.bind(
        null,
        props.filterTypeToShow,
        props.isDatePickerDrawerVisible,
    );

    return (
        <>
            {shouldShow(DATE_FILTER) && <DateFilters {...props} />}
            {shouldShow(TAGS_FILTER) && <CategoryFilters {...props} />}
            {shouldShow(FORMAT_FILTER) && <FormatFilters {...props} />}
            {shouldShow(LANGUAGE_FILTER) && <LanguageFilters {...props} />}
            {shouldShow(CURRENCY_FILTER) && <CurrencyFilters {...props} />}
            {shouldShow(PRICE_FILTER) && <PriceFilters {...props} />}
        </>
    );
};

Filters.propTypes = {
    setDatePickerDrawerVisible: PropTypes.func,
    isDatePickerDrawerVisible: PropTypes.bool,
    onFilterChange: PropTypes.func,
    onCustomDateChange: PropTypes.func,
    isMobile: PropTypes.bool,
    filterTypeToShow: PropTypes.string,
    location: PropTypes.object,
};

export default withRouter(Filters);
